import React, { Fragment } from 'react';
import { Popper } from 'react-popper';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { isFunction, isEmpty, toNumber, isUndefined, get } from 'lodash';

// local dependencies
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { OutsideClickHandler } from 'vendors/OutsideClickHandler';
import { messageBus } from 'core/modules/message-bus';
import { scrollListenerUtil } from 'utils/scrollListener';

/**
 * @type {function}
 * @desc This component is mounted globally and listens to message bus events
 * to open a React Component as a flyout using `react-popper`.
 *
 * @example
 * import { PopoverMounter } from 'modules/popover';
 */
export class PopoverMounter extends React.Component {
    constructor( props ) {
        log.debug( 'PopoverMounter.constructor()' );

        super( props );

        // component state
        this.state = this.getDefaultState();

        // component data
        this.data = this.getDefaultData();

        // bind
        this.__bind();
    }

    /**
     * @desc bind events and methods
     */
    __bind() {
        log.debug( 'PopoverMounter.__bind()' );

        messageBus.on( MESSAGE_BUS_EVENTS.OPEN_POPOVER, this.handlePopoverOpen, this );
        messageBus.on( MESSAGE_BUS_EVENTS.UPDATE_POPOVER, this.handlePopoverUpdate, this );
        messageBus.on( MESSAGE_BUS_EVENTS.CLOSE_POPOVER, this.handlePopoverClose, this );

        this.handlePopoverOpen = this.handlePopoverOpen.bind( this );
        this.handlePopoverUpdate = this.handlePopoverUpdate.bind( this );
        this.handlePopoverClose = this.handlePopoverClose.bind( this );
        this.preventEventPropagation = this.preventEventPropagation.bind( this );
        this.handleOutsideClick = this.handleOutsideClick.bind( this );

        // should hide on scroll
        if( this.props.hideOnScroll ) {
            scrollListenerUtil.listen( this.props.scrollParent, this.handlePopoverClose );
        }
    }

    /**
     * @desc unbind events
     */
    __unbind() {
        log.debug( 'PopoverMounter.__unbind()' );

        messageBus.off( MESSAGE_BUS_EVENTS.OPEN_POPOVER, this.handlePopoverOpen, this );
        messageBus.off( MESSAGE_BUS_EVENTS.UPDATE_POPOVER, this.handlePopoverUpdate, this );
        messageBus.off( MESSAGE_BUS_EVENTS.CLOSE_POPOVER, this.handlePopoverClose, this );

        // should hide on scroll
        if( this.props.hideOnScroll ) {
            scrollListenerUtil.unlisten( this.props.scrollParent, this.handlePopoverClose );
        }
    }

    /********************************************/

    /**
     * @desc return default popover state
     */
    getDefaultState() {
        return {
            isOpen: false,
            payload: {}
        };
    }

    /**
     * @desc return default component data
     */
    getDefaultData() {
        return {
            scheduleUpdateFn: null // popper manual position update function
        };
    }

    /**
     * @desc change state to display popover content
     * @param {*} payload
     */
    handlePopoverOpen( payload ) {
		log.debug( 'PopoverMounter.handlePopoverOpen()' );

		if( this.state.isOpen ) {
            this.handlePopoverClose();
		}

		if( payload.id !== this.state.payload.id ) {

			// timeout is to run popover close first as outside click may run close callback first
            setTimeout( () => {
                this.setState( {
                    isOpen: true,
                    payload: payload
                }, () => {
                    this.lockContainer( { isOpened: true, scrollElement: this.state.payload.scrollElement } );
                    payload.onOpen(); // callback for open
                } );
            } );
		}
    }

    /**
     * @desc When a popover is opened, lock scroll of a container
     */
    lockContainer( { isOpened, scrollElement } ) {
        if( ! isEmpty( scrollElement ) ) {
            const container = document.querySelector( scrollElement );
            const hiddenClass = 'ui-overflow-hidden';

            if( isOpened ) {
                container.classList.add( hiddenClass );
            } else {
                container.classList.remove( hiddenClass );
            }
        }
    }

    /**
     * @desc update mounted view `props`
     */
    handlePopoverUpdate( newProps ) {
		log.debug( 'PopoverMounter.handlePopoverUpdate()' );

        if( this.state.isOpen ) {
            this.setState( {
                payload: {
                    ...this.state.payload,
                    props: {
                        ...this.state.payload.props,
                        ...newProps
                    }
                }
            } );
        }
    }

    /**
     * @desc revert popover state to default, which hides it
     */
    handlePopoverClose( event ) {
        log.debug( 'PopoverMounter.handlePopoverClose()' );

        // prevent event propagation
        if( ! isEmpty( event ) ) {
            this.preventEventPropagation( event );
        }

        if( this.state.isOpen ) {
            const defaultState = this.getDefaultState();
            const defaultData = this.getDefaultData();

           this.state.payload.onClose(); // callback for close

            const scrollElement = this.state.payload.scrollElement;

            this.setState( defaultState, () => {
                this.lockContainer( { isOpened: false, scrollElement } );
            } ); // set default state

            this.data = defaultData; // set default data
        }
    }

    /**
     * @desc Function which handles outside click in popover with a delay
     */
    handleOutsideClick() {
        log.debug( 'PopoverMounter.handleOutsideClick()' );
        const delay = isUndefined( this.state.payload.delay ) ? 0: this.state.payload.delay;

        // timeout is to run popover close function with a delay as handlePopoverClose was being called first instead of dropdown close
        setTimeout( () => {
            this.handlePopoverClose();
        }, delay );
    }

    /**
     * @desc when trigger goes out of screen, hide popover (async as it is called from render function)
     */
    handlePopoverOverflow() {
        log.debug( 'PopoverMounter.handlePopoverOverflow()' );

        setTimeout( () => {
            this.handlePopoverClose();
        } );
    }

    /**
     * @desc Prevent event progation when clicked on backdrop.
     */
    preventEventPropagation( event ) {
        event.stopPropagation();
        event.preventDefault();
        return false;
    }

    /********************************************/

    /**
     * @desc When component mounts, unbind everything
     */
    componentWillUnmount() {
        this.__unbind();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        log.debug( 'PopoverMounter.render()' );

        if( this.state.isOpen ) {

            /** @type { HTMLElement } */
            const triggerElem = this.state.payload.referenceElement;
            const triggerElemWidth = triggerElem.offsetWidth;

            /*************************/

            /** @type { CSSStyleDeclaration } */
            const popoverBodyStyle = {
                backgroundColor: this.state.payload.styles.backgroundColor,
                borderRadius: this.state.payload.styles.borderRadius,
                border: this.state.payload.styles.border,
                boxShadow: this.state.payload.styles.boxShadow,
                margin: this.state.payload.styles.margin
			};

			if( !isUndefined( this.state.payload.styles.width ) ) {
				popoverBodyStyle.width = this.state.payload.styles.width;
			}

            // compact mode
            if( this.state.payload.compact ) {
                popoverBodyStyle.width = triggerElemWidth;
            }

            const popoverClassName = classNames( 'ui-popover', this.state.payload.popoverClassName, {
                'ui-popover--no-tooltip': false === this.state.payload.tooltip,
                'ui-popover--isDropdown': this.state.payload.isDropdown,
                'ui-popover--lower-z-index': get( this.state.payload, 'props.lowerPopoverZIndex' )
            } );

            const popoverBodyClassName = classNames( 'ui-popover__body', this.state.payload.popoverBodyClassName, {
                'ui-popover__body--compact': this.state.payload.compact
            } );

            /*************************/

            return (
                createPortal(
                    <Popper
                        referenceElement={ triggerElem }
                        positionFixed={ true }
                        placement={ this.state.payload.placements[ 0 ] }

						// modifiers={ {
                        //     preventOverflow: {
                        //         enabled: true,
                        //         boundariesElement: 'viewport'
                        //     },
                        //     flip: {
                        //         behavior: this.state.payload.placements
                        //     },
                        //     offset: {
                        //         offset: this.state.payload.offset
                        //     }
                        // } }
						modifiers ={ [
							{
							name: 'offset',
							options: {
								offset: this.state.payload.offset
							},
							},
						] }
                    >
                        {
                            ( { ref, style, placement, arrowProps, outOfBoundaries, scheduleUpdate } ) => {

                                // save `scheduleUpdateFn` function and pass it to parent via callback function
                                if( null === this.data.scheduleUpdateFn && isFunction( this.state.payload.handleForceUpdate ) ) {
                                    this.data.scheduleUpdateFn = scheduleUpdate;
                                    this.state.payload.handleForceUpdate( this.data.scheduleUpdateFn );
                                }

                                // when popover goes out of viewport boundaries => handle it
                                if( true === outOfBoundaries && true === this.state.payload.closeOnOverflow ) {
                                    this.handlePopoverOverflow();
                                }

                                return (
                                    <Fragment>

                                        {/* backdrop */}
                                        {
                                            0 < toNumber( this.state.payload.styles.backdropOpacity ) ? (
                                                <div
                                                    className='ui-popover__backdrop'
                                                    style={ { backgroundColor: `rgba(0, 0, 0, ${ this.state.payload.styles.backdropOpacity })` } }
                                                    onClick={ this.preventEventPropagation }
                                                />
                                            ) : null
                                        }

                                        <OutsideClickHandler disabled={ this.state.payload.outsideClickDisabled } onOutsideClick={ this.handleOutsideClick }>
                                            <div className={ popoverClassName } ref={ ref } style={ style } data-placement={ placement }>

                                                    {/* body */}
                                                    <div className={ popoverBodyClassName } style={ popoverBodyStyle }>
                                                        <this.state.payload.view
                                                            { ...this.state.payload.props }
                                                            closePopover={ this.handlePopoverClose }
                                                            placement = { placement }
                                                        />
                                                    </div>

                                                    {   // arrow
                                                        this.state.payload.tooltip ? (
                                                            <div
                                                                className='ui-popover__arrow'
                                                                ref={ arrowProps.ref }
                                                                style={ { ...arrowProps.style, borderColor: this.state.payload.styles.arrowColor } }
                                                            />
                                                        ) : null
                                                    }

                                                </div>
                                        </OutsideClickHandler>
                                    </Fragment>
                                );
                            }
                        }
                    </Popper>,

                    // append to the body
                    document.body
                )
            );
        } else {
            return null;
        }
    }
}

// set display name
PopoverMounter.displayName = 'PopoverMounter';

// default props
PopoverMounter.defaultProps = {};

// prop types
PopoverMounter.propTypes = {};
